import type { FileUploadResponse } from '../../../../chats'
import type { QueryResponse } from '../../queries'
import type { UseMutationResult } from '@tanstack/react-query'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import http from '../../../../../lib/http'
import { invariant } from '../../../../chats'
import { documentQueryKey } from '../../queries'

interface ErrorResponse {
  message: string
  error: true
}

interface UploadDocumentOptions {
  patientId: string
  clientName: string
}

export interface UploadDocumentVariables {
  file?: File | null
  description?: string
  created?: string
  type: { value: string, label: string }
  notVisible?: boolean
}

enum SecurityLabels {
  normal = 'normal',
  restricted = 'restricted',
  veryRestricted = 'veryRestricted',
}

function isErrorResponse (data: unknown): data is ErrorResponse {
  return typeof data === 'object' && data !== null && 'error' in data && 'message' in data
}

const securityLabels: Record<SecurityLabels, { code: string, display: string, system: string }> = {
  normal: {
    code: 'N',
    display: 'normal',
    system: 'http://hl7.org/fhir/v3/Confidentiality'
  },
  restricted: {
    code: 'R',
    display: 'restricted',
    system: 'http://hl7.org/fhir/v3/Confidentiality'
  },
  veryRestricted: {
    code: 'V',
    display: 'very restricted',
    system: 'http://hl7.org/fhir/v3/Confidentiality'
  }
}

export function useUploadDocumentMutation (options: UploadDocumentOptions): UseMutationResult<unknown, unknown, UploadDocumentVariables | undefined, unknown> {
  const { patientId, clientName } = options
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, UploadDocumentVariables | undefined, unknown>({
    async mutationFn (variables) {
      invariant(typeof patientId === 'string' && patientId.length > 0, 'patientId is required')
      invariant(typeof clientName === 'string' && clientName.length > 0, 'clientId is required')
      invariant(variables?.file, 'file is required')

      const file = variables?.file
      const description = variables?.description
      const created = variables?.created

      let uploadData: FileUploadResponse['files'] | undefined

      let formData = new FormData()

      formData.append('file0', file, file.name)

      let fileResponse: FileUploadResponse = await http.postRequest('/documents', formData, { clientName })
      uploadData = fileResponse.files

      if (uploadData?.file0) {
        const data: Record<string, unknown> = {
          resourceType: 'DocumentReference',
          content: [
            {
              attachment: {
                creation: new Date().toISOString(),
                contentType: uploadData.file0.mime_type,
                size: uploadData.file0.size,
                title: uploadData.file0.filename,
                hash: uploadData.file0.id,
                language: 'en'
              }
            }
          ],
          type: {
            coding: [
              {
                code: variables.type?.value,
                display: variables.type?.label
              }
            ]
          },
          securityLabel: [
            {
              coding: [
                variables.notVisible
                  ? securityLabels[SecurityLabels.restricted]
                  : securityLabels[SecurityLabels.normal]
              ]
            }
          ],
          indexed: new Date().toISOString(),
          meta: {
            security: [ securityLabels[SecurityLabels.veryRestricted] ]
          },
          status: 'current',
          subject: {
            reference: `Patient/${patientId}`
          }
        }

        if (description) {
          data.description = description
        }

        if (created) {
          data.created = created
        }

        return await http.postRequest(`/behavioral/${patientId}/documents`, data)
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: documentQueryKey({ patientId }) })
    },
    onSuccess: async (data) => {
      if (isErrorResponse(data)) {
        toast.error(data?.message, { position: 'bottom-center', duration: 4000 })
        return
      }
      toast.success('Document added successfully', { position: 'bottom-center', duration: 4000 })
    },
    onError: () => {
      toast.error('Something went wrong while saving the file', { position: 'bottom-center' })
    }
  })
}

export function useDeleteDocument (): UseMutationResult<unknown, unknown, { id: string, patientId: string }, unknown> {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { id: string, patientId: string }, unknown>({
    async mutationFn (variables) {
      invariant(variables.id, 'Document ID is required')
      invariant(variables.patientId, 'Patient ID is required')

      return await http.deleteRequest(`/behavioral/${variables.patientId}/documents/${variables.id}`)
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: documentQueryKey({ patientId: variables.patientId }) })
    },
    onSuccess: async (data, variables, context) => {
      if (isErrorResponse(data)) {
        toast.error(data?.message, { position: 'bottom-center', duration: 4000 })
        return
      }

      toast.success('Document deleted successfully', { position: 'bottom-center', duration: 4000 })
    },
    onError: () => {
      toast.error('Something went wrong while deleting the file', { position: 'bottom-center', duration: 4000 })
    }
  })
}

export function useEditDocumentMutation (options: UploadDocumentOptions): UseMutationResult<unknown, unknown, (UploadDocumentVariables & {id: string}) | undefined, unknown> {
  const { patientId, clientName } = options
  const queryClient = useQueryClient()

  return useMutation({
    async mutationFn (variables) {
      invariant(typeof patientId === 'string' && patientId.length > 0, 'patientId is required')
      invariant(typeof clientName === 'string' && clientName.length > 0, 'clientId is required')

      const description = variables?.description
      const created = variables?.created

      const data: Record<string, unknown> = {
        type: {
          coding: [
            {
              code: variables?.type?.value,
              display: variables?.type?.label
            }
          ]
        },
        securityLabel: [
          {
            coding: [
              variables?.notVisible
                ? securityLabels[SecurityLabels.restricted]
                : securityLabels[SecurityLabels.normal]
            ]
          }
        ],
        meta: {
          security: [ securityLabels[SecurityLabels.veryRestricted] ]
        }
      }

      if (description) {
        data.description = description
      }

      if (created) {
        data.created = created
      }

      return await http.putRequest<ErrorResponse>(`/behavioral/${patientId}/documents/${variables?.id ?? ''}`, data)
    },
    onMutate: async (payload) => {
      const prevDocument = queryClient.getQueriesData<QueryResponse>({ queryKey: documentQueryKey({ patientId }), exact: false })
      queryClient.setQueriesData<QueryResponse>({ queryKey: documentQueryKey({ patientId }), exact: false }, (state) => {
        const index = state?.data.findIndex((el) => el.id === payload?.id)

        if (!index || index === -1) {
          return state
        }

        const element = state?.data[index]

        if (!element) {
          return state
        }

        return {
          ...state,
          data: [
            ...state.data.slice(0, index) ?? [],
            {
              ...element,
              created: payload?.created ?? element.created,
              document_type: payload?.type?.value ?? element.document_type,
              description: payload?.description ?? element.description,
              notVisible: payload?.notVisible ?? element.notVisible
            },
            ...state?.data.slice(index + 1) ?? []
          ]
        }
      })

      return {
        prevDocument
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: documentQueryKey({ patientId }) })
    },
    onSuccess: async (data) => {
      if (isErrorResponse(data)) {
        toast.error(data?.message, { position: 'bottom-center', duration: 4000 })
        return
      }
      toast.success('Document edited successfully', { position: 'bottom-center', duration: 4000 })
    },
    onError: (_, __, context): void => {
      toast.error('Something went wrong while updating the file', { position: 'bottom-center' })
      if (context?.prevDocument) {
        let [ queryKey, data ] = context.prevDocument

        if (!queryKey) {
          return
        }

        queryClient.setQueryData(queryKey, data)
      }
    }
  })
}
